import { type Category, NAME_POLICY, textSafety } from '@mogzo/text-safety';
import { ADJECTIVES, NOUNS } from './username-words';

/**
 * Usernames: the one public name on MOGZO, on posts, comments, leaderboards and in games. No
 * real names are needed. Letters, digits and `_`, unique whatever the case (`SkyRunner` and
 * `skyrunner` are the same name), shown as the player typed it.
 */
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 20;
/** A username can be changed once in this many days. */
export const USERNAME_CHANGE_DAYS = 30;

const USERNAME_PATTERN = /^[A-Za-z0-9_]+$/;

/** Names for MOGZO itself and its staff, which nobody can take. */
const RESERVED = new Set([
  'admin',
  'administrator',
  'anonymous',
  'deleted',
  'developer',
  'everyone',
  'guest',
  'help',
  'helpdesk',
  'mod',
  'moderator',
  'moderators',
  'null',
  'official',
  'owner',
  'reviewer',
  'root',
  'security',
  'staff',
  'super_admin',
  'superadmin',
  'support',
  'system',
  'team',
  'undefined',
]);
export type UsernameProblem = 'length' | 'characters' | 'reserved' | 'not_allowed';

export const USERNAME_PROBLEM_MESSAGES: Record<UsernameProblem, string> = {
  length: `Use ${USERNAME_MIN_LENGTH} to ${USERNAME_MAX_LENGTH} characters.`,
  characters: 'Use only letters, numbers and _.',
  reserved: 'Names with MOGZO or Quarkfox, and names like “admin”, are kept for us.',
  not_allowed: 'Pick a different name.',
};

export interface UsernameCheck {
  problem: UsernameProblem | null;
  /** What text safety found, for logs and counts; never shown to the player. */
  categories: Category[];
}

/**
 * Checks a username against the rules and text safety's name policy, which undoes disguises
 * (`sh1t`, `f_u_c_k`, look-alike letters) and keeps staff words (`AdminJoe`) and MOGZO's own
 * names out. Whether someone already has it is the caller's check.
 */
export function checkUsername(name: string): UsernameCheck {
  if (name.length < USERNAME_MIN_LENGTH || name.length > USERNAME_MAX_LENGTH) {
    return { problem: 'length', categories: [] };
  }
  if (!USERNAME_PATTERN.test(name)) return { problem: 'characters', categories: [] };
  if (RESERVED.has(name.toLowerCase())) return { problem: 'reserved', categories: [] };
  const { categories } = textSafety.check(name, NAME_POLICY);
  if (!categories.length) return { problem: null, categories };
  // Only staff words or MOGZO's names: say it's kept for us. Anything else: just pick another.
  const reserved = categories.every((category) => category === 'impersonation');
  return { problem: reserved ? 'reserved' : 'not_allowed', categories };
}

/** What's wrong with a username, or null when it can be used (if nobody has it). */
export function usernameProblem(name: string): UsernameProblem | null {
  return checkUsername(name).problem;
}

const pick = <T>(list: readonly T[], random: () => number): T =>
  list[Math.floor(random() * list.length)] as T;

/**
 * A made-up username for a new account: an adjective, a noun and four digits
 * (`SwiftComet4821`). Over 600 million of them; a pair that runs into a blocked word is skipped.
 * Whether one is free is the caller's check.
 */
export function generateUsername(random: () => number = Math.random): string {
  for (;;) {
    const digits = String(Math.floor(random() * 10_000)).padStart(4, '0');
    const name = `${pick(ADJECTIVES, random)}${pick(NOUNS, random)}${digits}`;
    if (usernameProblem(name) === null) return name;
  }
}
